import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import "../../css/Home.css";
import { useAxiosInstance } from "../../services/Interceptor";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PasswordInput from "../../components/common/PasswordInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { highschoolRegisterCoach } from "../../services/CoachService";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const phone10Regex = /^\d{10}$/;

const NewCoachRegistration = () => {
  const apiInstance = useAxiosInstance();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("register_token")?.trim() || "";
  const emailFromUrl = (searchParams.get("email") || "").trim();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Registration token is missing/invalid.", {
        position: "top-right",
      });
    }
    if (!emailFromUrl || !emailRegex.test(emailFromUrl)) {
      toast.error("Email in the link is missing/invalid.", {
        position: "top-right",
      });
    }
  }, [token, emailFromUrl]);

  const onHandleSubmit = async (data) => {
    if (!token) {
      toast.error("Registration token is missing.", { position: "top-right" });
      return;
    }
    if (!emailFromUrl || !emailRegex.test(emailFromUrl)) {
      toast.error("Invalid email in link.", { position: "top-right" });
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match!", { position: "top-center" });
      return;
    }

    if (!phone10Regex.test(data.phoneNumber)) {
      toast.error("Enter a valid 10-digit phone number.", {
        position: "top-center",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        token,
        email: emailFromUrl,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        phoneNumber: data.phoneNumber.trim(),
        password: data.password,
      };

      await highschoolRegisterCoach(apiInstance, payload);

      toast.success("Coach registration successful.", {
        position: "top-right",
      });
      reset();
      navigate("/");
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Registration failed. Please try again.";
      toast.error(msg, { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#2f2f2f] py-12">
        <div className="max-w-4xl mx-auto px-6">
          <form
            className="container mx-auto px-4 bg-[#3a3a3a] rounded-lg shadow-lg p-5"
            onSubmit={handleSubmit(onHandleSubmit)}
          >
            <div className="px-2 mb-20">
              <div className="text-center text-red-500 font-extrabold uppercase tracking-wide text-xl mt-6">
                COACH REGISTRATION
              </div>

              <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-6">
                <div className="form-field mt-4">
                  <label htmlFor="firstname">First Name</label>
                  <input
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                    placeholder="First Name"
                  />
                  {errors.firstName && (
                    <span className="text-red-500">
                      {errors.firstName.message}
                    </span>
                  )}
                </div>

                <div className="form-field mt-4">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                    placeholder="Last Name"
                  />
                  {errors.lastName && (
                    <span className="text-red-500">
                      {errors.lastName.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field mt-4">
                <label htmlFor="email">Email</label>
                <input value={emailFromUrl} readOnly placeholder="Email" />
                {!emailFromUrl || !emailRegex.test(emailFromUrl) ? (
                  <span className="text-red-500">Valid email is required</span>
                ) : null}
              </div>

              <div className="form-field mt-4">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Phone Number"
                  {...register("phoneNumber", {
                    required: "Phone number is required",
                    pattern: {
                      value: phone10Regex,
                      message: "Enter a valid 10-digit phone number",
                    },
                    onChange: (e) => {
                      const digits = String(e.target.value || "")
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      e.target.value = digits;
                      setValue("phoneNumber", digits, { shouldValidate: true });
                    },
                  })}
                />
                {errors.phoneNumber && (
                  <span className="text-red-500">
                    {errors.phoneNumber.message}
                  </span>
                )}
              </div>

              <PasswordInput
                label="Password"
                name="password"
                register={register}
                validation={{
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters long",
                  },
                }}
                placeholder="Password"
                errors={errors}
                inputClassName="px-4 py-2 w-full border rounded"
              />

              <PasswordInput
                label="Confirm Password"
                name="confirmPassword"
                register={register}
                validation={{
                  required: "Confirm password is required",
                  validate: (v) =>
                    v === watch("password") || "Passwords do not match",
                }}
                placeholder="Confirm Password"
                errors={errors}
                inputClassName="px-4 py-2 w-full border rounded"
              />

              <div className="form-field mt-4">
                {loading && <LoadingSpinner />}
              </div>

              <button
                type="submit"
                disabled={
                  loading ||
                  !token ||
                  !emailFromUrl ||
                  !emailRegex.test(emailFromUrl)
                }
                className="bg-red-500 text-white uppercase rounded px-8 py-2 text-center hover:bg-red-700 cursor-pointer mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Submit
              </button>
            </div>
          </form>

          <ToastContainer />
        </div>
      </div>
    </>
  );
};

export default NewCoachRegistration;
